import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { BugReportsTable } from '../../components/BugReport';
import { useAuth } from '../../contexts/authContext';
import { useSharePointAuth } from '../../contexts/sharePointAuthContext';
import { useSharePoint } from '../../hooks/useSharePoint';
import { isAdmin } from '../PolicyConfig/utils/adminAccess';
import { fetchSSOConfig, getDefaultConfig, parseSAMLMetadata, SSOConfigApiError, saveSSOConfig, validateConfig } from '../SSOConfigPage/api';
import { ApiSettings, AppearanceSettings, DataSettings, NotificationSettings, PolicyConfigSettings, ProfileSettings, SecuritySettings, SettingsLayout, SharePointSettings, SSOSettings } from './components';
export default function Settings() {
    const { user } = useAuth();
    const { isSharePointConnected, connectSharePoint, disconnectSharePoint: logoutSharePoint } = useSharePointAuth();
    const { getUserSiteAccess } = useSharePoint();
    // ============ Helper Functions ============
    // Support both port-based (staging) and domain-based (sprezz) deployments
    const getAuthServiceUrl = ()=>{
        const origin = window.location.origin;
        // For domain-based deployments: agents.sprezz.specra.ai → auth.sprezz.specra.ai
        if (origin.includes('agents.sprezz.specra.ai')) {
            return origin.replace('agents.sprezz.specra.ai', 'auth.sprezz.specra.ai');
        }
        // For domain-based deployments: agents.staging.specra.ai → auth.staging.specra.ai
        if (origin.includes('agents.staging.specra.ai')) {
            return origin.replace('agents.staging.specra.ai', 'auth.staging.specra.ai');
        }
        // For port-based deployments: localhost:3001 → localhost:9091
        return origin.replace(':3001', ':9091');
    };
    // ============ State Management ============
    const [searchParams] = useSearchParams();
    const sectionParam = searchParams.get('section');
    const [activeSection, setActiveSection] = useState(sectionParam || 'profile');
    const [hasChanges, setHasChanges] = useState(false);
    // Profile identity is read-only and comes from the signed-in user (managed by
    // the org / IdP — there is no self-service profile-update RPC). Timezone is
    // the one genuinely user-owned preference, persisted locally.
    const TIMEZONE_STORAGE_KEY = 'specra-timezone';
    const [timezone, setTimezone] = useState(()=>{
        try {
            return window.localStorage.getItem(TIMEZONE_STORAGE_KEY) || 'America/New_York';
        } catch  {
            return 'America/New_York';
        }
    });
    // Notifications
    const [notifications, setNotifications] = useState({
        emailNotifications: true,
        pushNotifications: false,
        documentProcessing: true,
        systemUpdates: true,
        securityAlerts: true
    });
    // Security
    const [security, setSecurity] = useState({
        sessionTimeout: 30,
        loginNotifications: true
    });
    // Appearance
    const [darkMode, setDarkMode] = useState(false);
    const [showPasswords, setShowPasswords] = useState(false);
    // API Keys
    const [apiKeys, setApiKeys] = useState([
        {
            id: '1',
            name: 'Production API',
            key: 'sk-prod-1234567890abcdef',
            lastUsed: '2024-01-15T10:30:00Z',
            permissions: [
                'read',
                'write'
            ],
            active: true
        },
        {
            id: '2',
            name: 'Development API',
            key: 'sk-dev-abcdef1234567890',
            lastUsed: '2024-01-10T14:20:00Z',
            permissions: [
                'read'
            ],
            active: false
        }
    ]);
    // SharePoint
    const [sharepointConnection, setSharePointConnection] = useState({
        connected: false,
        email: null,
        tenantId: null,
        lastConnected: null
    });
    const [sharepointSites, setSharePointSites] = useState([]);
    // SSO
    const [ssoConfig, setSsoConfig] = useState(getDefaultConfig());
    const [ssoSecrets, setSsoSecrets] = useState({
        google_client_secret: '',
        microsoft_client_secret: ''
    });
    const [ssoLoading, setSsoLoading] = useState(false);
    const [ssoError, setSsoError] = useState(null);
    const [ssoSuccess, setSsoSuccess] = useState(null);
    const [isUploadingXML, setIsUploadingXML] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const TENANT_SLUG = 'default';
    const AUTH_SERVICE_URL = getAuthServiceUrl();
    // ============ Event Handlers ============
    // Profile handlers — only the timezone preference is editable.
    const handleTimezoneChange = (value)=>{
        setTimezone(value);
        setHasChanges(true);
    };
    // Notification handlers
    const handleNotificationChange = (field)=>{
        setNotifications((prev)=>({
                ...prev,
                [field]: !prev[field]
            }));
        setHasChanges(true);
    };
    // Security handlers
    const handleSecurityChange = (field, value)=>{
        setSecurity((prev)=>({
                ...prev,
                [field]: value
            }));
        setHasChanges(true);
    };
    // API Key handlers
    const generateApiKey = ()=>{
        const newKey = {
            id: Date.now().toString(),
            name: `API Key ${apiKeys.length + 1}`,
            key: `sk-${Math.random().toString(36).substring(7)}-${Math.random().toString(36).substring(7)}`,
            lastUsed: new Date().toISOString(),
            permissions: [
                'read'
            ],
            active: true
        };
        setApiKeys((prev)=>[
                ...prev,
                newKey
            ]);
    };
    const toggleApiKey = (id)=>{
        setApiKeys((prev)=>prev.map((key)=>key.id === id ? {
                    ...key,
                    active: !key.active
                } : key));
    };
    const deleteApiKey = (id)=>{
        if (window.confirm('Are you sure you want to delete this API key?')) {
            setApiKeys((prev)=>prev.filter((key)=>key.id !== id));
        }
    };
    // Handle OAuth redirect query params (error/success)
    const oauthError = searchParams.get('error');
    const showOAuthError = sectionParam === 'sharepoint' && oauthError;
    // Sync SharePoint connection state with OAuth context and load real sites
    useEffect(()=>{
        if (isSharePointConnected) {
            setSharePointConnection({
                connected: true,
                email: user?.email || null,
                tenantId: user?.tenant_id || null,
                lastConnected: new Date().toISOString()
            });
            // Load real sites from Graph API
            if (user?.id && user?.tenant_id) {
                getUserSiteAccess(user.id, user.tenant_id).then((access)=>{
                    if (access?.sites) {
                        setSharePointSites(access.sites);
                    }
                });
            }
        } else {
            setSharePointConnection({
                connected: false,
                email: null,
                tenantId: null,
                lastConnected: null
            });
            setSharePointSites([]);
        }
    }, [
        isSharePointConnected,
        user,
        getUserSiteAccess
    ]);
    // SharePoint handlers
    const disconnectSharePoint = async ()=>{
        try {
            await logoutSharePoint();
            setSharePointConnection({
                connected: false,
                email: null,
                tenantId: null,
                lastConnected: null
            });
            setSharePointSites([]);
        } catch  {
        // Error toast already shown by the context
        }
    };
    // GetSSOConfig is admin-only in mariejois, so asking as anyone else earns a
    // guaranteed 403. This route is not admin-gated — every user lands on
    // /settings — so the request has to be gated here instead.
    //
    // It used to be invisible: express answered a missing config with a 404,
    // which the catch below turns into defaults without a word. mariejois refuses
    // by role rather than reporting absence, so the same call now surfaces as an
    // error the page does not show, which is exactly what the network gate
    // flags. Not asking is better than either.
    const canManageSSO = isAdmin(user);
    // SSO handlers
    const handleFetchSSOConfig = useCallback(async ()=>{
        if (!canManageSSO) {
            setSsoConfig(getDefaultConfig());
            setSsoLoading(false);
            return;
        }
        setSsoLoading(true);
        setSsoError(null);
        try {
            const data = await fetchSSOConfig();
            setSsoConfig(data);
        } catch (err) {
            if (err instanceof SSOConfigApiError && err.status === 404) {
                setSsoConfig(getDefaultConfig());
            } else {
                setSsoError(err instanceof Error ? err.message : 'Failed to fetch SSO configuration');
            }
        } finally{
            setSsoLoading(false);
        }
    }, [
        canManageSSO
    ]);
    useEffect(()=>{
        handleFetchSSOConfig();
    }, [
        handleFetchSSOConfig
    ]);
    const handleSaveSSOConfig = async ()=>{
        setSsoLoading(true);
        setSsoError(null);
        setSsoSuccess(null);
        const validationError = validateConfig(ssoConfig, ssoSecrets);
        if (validationError) {
            setSsoError(validationError);
            setSsoLoading(false);
            return;
        }
        try {
            const payload = {
                ...ssoConfig,
                google_client_secret: ssoSecrets.google_client_secret || undefined,
                microsoft_client_secret: ssoSecrets.microsoft_client_secret || undefined
            };
            const savedConfig = await saveSSOConfig(payload);
            setSsoConfig(savedConfig);
            setSsoSuccess('SSO configuration saved successfully!');
            setSsoSecrets({
                google_client_secret: '',
                microsoft_client_secret: ''
            });
        } catch (err) {
            setSsoError(err instanceof Error ? err.message : 'Failed to save SSO configuration');
        } finally{
            setSsoLoading(false);
        }
    };
    const handleMicrosoftXMLUpload = async (event)=>{
        const file = event.target.files?.[0];
        if (!file) return;
        setIsUploadingXML(true);
        setUploadError(null);
        try {
            const text = await file.text();
            const parsed = await parseSAMLMetadata(text, 'microsoft');
            setSsoConfig({
                ...ssoConfig,
                microsoft_saml_enabled: true,
                microsoft_saml_entity_id: parsed.entity_id,
                microsoft_saml_sso_url: parsed.sso_url,
                microsoft_saml_certificate: parsed.certificate
            });
            setSsoSuccess('Microsoft SAML XML uploaded successfully!');
            // Scroll to Microsoft SAML section after a brief delay
            setTimeout(()=>{
                const element = document.getElementById('microsoft-saml-section');
                if (element) {
                    element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                }
            }, 100);
        } catch (err) {
            setUploadError(err instanceof Error ? err.message : 'Failed to parse metadata file');
        } finally{
            setIsUploadingXML(false);
            event.target.value = '';
        }
    };
    // Main save handler. Persists what actually has local persistence today (the
    // timezone preference); other sections' backing stores are follow-ups.
    const handleSaveSettings = ()=>{
        try {
            window.localStorage.setItem(TIMEZONE_STORAGE_KEY, timezone);
        } catch  {
        // Storage unavailable (private mode) — the preference stays session-local.
        }
        setHasChanges(false);
        toast.success('Preferences saved');
    };
    // ============ Render Content ============
    const renderContent = ()=>{
        switch(activeSection){
            case 'profile':
                return /*#__PURE__*/ _jsx(ProfileSettings, {
                    company: user?.company,
                    email: user?.email ?? '',
                    name: user?.name ?? '',
                    onTimezoneChange: handleTimezoneChange,
                    role: user?.role,
                    timezone: timezone
                });
            case 'notifications':
                return /*#__PURE__*/ _jsx(NotificationSettings, {
                    notifications: notifications,
                    onNotificationChange: handleNotificationChange
                });
            case 'security':
                return /*#__PURE__*/ _jsx(SecuritySettings, {
                    security: security,
                    onSecurityChange: handleSecurityChange
                });
            case 'appearance':
                return /*#__PURE__*/ _jsx(AppearanceSettings, {
                    darkMode: darkMode,
                    onDarkModeChange: setDarkMode
                });
            case 'data':
                return /*#__PURE__*/ _jsx(DataSettings, {});
            case 'api':
                return /*#__PURE__*/ _jsx(ApiSettings, {
                    apiKeys: apiKeys,
                    showPasswords: showPasswords,
                    onGenerateApiKey: generateApiKey,
                    onToggleApiKey: toggleApiKey,
                    onDeleteApiKey: deleteApiKey,
                    onToggleShowPasswords: ()=>setShowPasswords(!showPasswords)
                });
            case 'sharepoint':
                return /*#__PURE__*/ _jsx(SharePointSettings, {
                    connection: sharepointConnection,
                    sites: sharepointSites,
                    onConnect: connectSharePoint,
                    onDisconnect: disconnectSharePoint,
                    oauthError: showOAuthError ? decodeURIComponent(oauthError) : undefined
                });
            case 'sso':
                return /*#__PURE__*/ _jsx(SSOSettings, {
                    config: ssoConfig,
                    secrets: ssoSecrets,
                    loading: ssoLoading,
                    error: ssoError,
                    success: ssoSuccess,
                    authServiceUrl: AUTH_SERVICE_URL,
                    tenantSlug: TENANT_SLUG,
                    onConfigChange: setSsoConfig,
                    onSecretsChange: setSsoSecrets,
                    onSave: handleSaveSSOConfig,
                    isUploadingXML: isUploadingXML,
                    uploadError: uploadError,
                    onXMLUpload: handleMicrosoftXMLUpload
                });
            case 'authorization':
                return /*#__PURE__*/ _jsx(PolicyConfigSettings, {});
            case 'users':
                // User management moved to the unified /admin console (polish task 04).
                // Redirect any bookmarked ?section=users deep link there.
                return /*#__PURE__*/ _jsx(Navigate, {
                    to: "/admin/people",
                    replace: true
                });
            case 'bug-reports':
                return /*#__PURE__*/ _jsx(BugReportsTable, {});
            default:
                return /*#__PURE__*/ _jsx(ProfileSettings, {
                    company: user?.company,
                    email: user?.email ?? '',
                    name: user?.name ?? '',
                    onTimezoneChange: handleTimezoneChange,
                    role: user?.role,
                    timezone: timezone
                });
        }
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(Helmet, {
                children: [
                    /*#__PURE__*/ _jsx("title", {
                        children: "Settings - GovCon AI"
                    }),
                    /*#__PURE__*/ _jsx("meta", {
                        name: "description",
                        content: "Application Settings and Preferences"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SettingsLayout, {
                activeSection: activeSection,
                onSectionChange: setActiveSection,
                hasChanges: hasChanges,
                onSave: handleSaveSettings,
                children: renderContent()
            })
        ]
    });
}
